type CaptureContext = {
   level: 'warning';
   tags: Record<string, string>;
   extra: Record<string, unknown>;
   fingerprint: string[];
};

type WatchOptions = {
   capture: (message: string, context: CaptureContext) => void;
};

type Culprit = { el: Element; right: number; width: number };

// Some margin for rounding noise (scrollbars, injected extension widgets, etc)
const OVERFLOW_THRESHOLD_PX = 8;
// Re-measure before reporting to avoid reporting animations/resizes
const CONFIRM_DELAY_MS = 2000;
const RECHECK_DEBOUNCE_MS = 500;
const MAX_CULPRITS = 10;
// scrollWidth is an integer, getBoundingClientRect isn't, so leave subpixel slack
const EDGE_TOLERANCE_PX = 2;
// A hidden or unsized viewport (the mobile app's off-screen cart-count WebView,
// an unsized iframe) measures clientWidth 0, so every box reads as overflow.
// The floor sits under the narrowest real phone, a folded Galaxy Fold at 280px.
const MIN_VIEWPORT_WIDTH_PX = 240;
// Group by what overflowed, so each layout bug gets its own issue instead of
// one that can never be closed. Capped because a Tailwind element carries
// twenty classes and the churning tail would scatter one bug across issues.
const MAX_FINGERPRINT_CLASSES = 3;
// A content id carries a row number: s84021 on a guide step, stepEmbed-123,
// review-IF145-307-4, and useId() counts up the tree. Keeping one would open
// an issue per step or per post, the spray the class cap exists to stop. Ids
// written by hand have no digits and do say where on the page an element sits.
const CONTENT_ID = /\d/;
// Tailwind's sm and lg breakpoints. The same element can fit at one width and
// overflow at another, and the fix differs, so widths group apart.
const VIEWPORT_BUCKETS = [
   { name: 'mobile', maxWidth: 639 },
   { name: 'tablet', maxWidth: 1023 },
];

const measureOverflow = () =>
   document.documentElement.scrollWidth - document.documentElement.clientWidth;

const isMeasurable = () => document.documentElement.clientWidth >= MIN_VIEWPORT_WIDTH_PX;

const viewportBucket = (width: number): string =>
   VIEWPORT_BUCKETS.find(bucket => width <= bucket.maxWidth)?.name ?? 'desktop';

const classesOf = (el: Element): string[] =>
   (el.getAttribute('class') ?? '').trim().split(/\s+/).filter(Boolean);

const selectorOf = (el: Element, id: string, classes: string[]): string =>
   `${el.tagName}${id ? `#${id}` : ''}${classes.length ? `.${classes.join('.')}` : ''}`;

const describeSelector = (el: Element): string => selectorOf(el, el.id, classesOf(el));

const fingerprintSelector = (el: Element): string =>
   selectorOf(
      el,
      CONTENT_ID.test(el.id) ? '' : el.id,
      classesOf(el).slice(0, MAX_FINGERPRINT_CLASSES)
   );

const describeCulprit = ({ el, right, width }: Culprit): string =>
   `${describeSelector(el)} right=${Math.round(right)} width=${Math.round(width)}`;

const isInsideSvg = (el: Element): boolean =>
   el instanceof SVGElement && el.ownerSVGElement !== null;

// Name the elements whose right edge lands on the document's overflow edge, so
// the Sentry event points at what pushed the page wide. Anchoring to that edge
// rather than to the viewport leaves out what sits past the viewport without
// widening the document: closed off-canvas panels, slides clipped inside a
// carousel, the icons riding along in them. Ties go to the narrowest match,
// which names the leaf ahead of the wrappers it stretched.
// O(n) over every element under `body`, but runs at most once per pageview and
// only after confirmed overflow, with the computed-style read limited to the
// elements past the viewport.
// Selectors only, no text content, so no user content reaches Sentry.
const findCulprits = (): Culprit[] => {
   const doc = document.documentElement;
   const viewportWidth = doc.clientWidth;
   const overflowEdge = doc.scrollWidth;
   const scrollX = window.scrollX;
   const matches: Culprit[] = [];
   for (const el of Array.from(document.body.getElementsByTagName('*'))) {
      if (isInsideSvg(el)) {
         continue;
      }
      const rect = el.getBoundingClientRect();
      const right = rect.right + scrollX;
      if (right <= viewportWidth + 1 || right > overflowEdge + EDGE_TOLERANCE_PX) {
         continue;
      }
      if (getComputedStyle(el).position === 'fixed') {
         continue;
      }
      matches.push({ el, right, width: rect.width });
   }
   const atEdge = matches.filter(({ right }) => right >= overflowEdge - EDGE_TOLERANCE_PX);
   const ranked = atEdge.length
      ? atEdge.sort((a, b) => a.width - b.width)
      : matches.sort((a, b) => b.width - a.width);
   return ranked.slice(0, MAX_CULPRITS);
};

/** Report horizontal overflow to Sentry at most once per pageview. */
export function watchForHorizontalOverflow({ capture }: WatchOptions): void {
   let reported = false;
   let confirmTimer: ReturnType<typeof setTimeout> | undefined;
   let debounceTimer: ReturnType<typeof setTimeout> | undefined;
   let resizeObserver: ResizeObserver | undefined;

   const report = () => {
      reported = true;
      window.removeEventListener('resize', scheduleCheck);
      resizeObserver?.disconnect();
      const culprits = findCulprits();
      const clientWidth = document.documentElement.clientWidth;
      const culprit = culprits.length ? fingerprintSelector(culprits[0].el) : 'unknown-culprit';
      // Sentry titles the issue from the message, so name the culprit there too.
      capture(`Page has unexpected horizontal overflow: ${culprit}`, {
         level: 'warning',
         tags: { overflowDiagnostic: 'true' },
         extra: {
            overflowPx: measureOverflow(),
            clientWidth,
            scrollWidth: document.documentElement.scrollWidth,
            innerWidth: window.innerWidth,
            culprits: culprits.map(describeCulprit),
         },
         fingerprint: ['horizontal-overflow', viewportBucket(clientWidth), culprit],
      });
   };

   const overflowing = () => isMeasurable() && measureOverflow() > OVERFLOW_THRESHOLD_PX;

   const check = () => {
      if (reported || confirmTimer || !overflowing()) {
         return;
      }
      confirmTimer = setTimeout(() => {
         confirmTimer = undefined;
         if (!reported && overflowing()) {
            report();
         }
      }, CONFIRM_DELAY_MS);
   };

   const scheduleCheck = () => {
      if (reported) {
         return;
      }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(check, RECHECK_DEBOUNCE_MS);
   };

   const start = () => {
      check();
      window.addEventListener('resize', scheduleCheck);
      if (typeof ResizeObserver !== 'undefined') {
         resizeObserver = new ResizeObserver(scheduleCheck);
         resizeObserver.observe(document.body);
      }
   };

   const fontsSettled = () => (document.fonts ? document.fonts.ready : Promise.resolve());
   if (document.readyState === 'complete') {
      void fontsSettled().then(start);
   } else {
      window.addEventListener('load', () => void fontsSettled().then(start), { once: true });
   }
}
